import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import beActivateTagsTrial from 'actions/billing/beActivateTagsTrial.action';
import beGetTagsTrial from 'actions/billing/beGetTagsTrial.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Space from 'components/generic/Space.component';
import Spinner from 'components/generic/Spinner.component';
import DELAY from 'constants/delay.const';
import S from 'constants/string.const';
import { FeStack } from 'errors/fe.error';
import useTip from 'hooks/useTip.hook';
import {
  selectCurrentSalonBilling,
  selectCurrentSalonId,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectBillingError, selectBillingPlan, selectBillingTrial } from 'selectors/settings.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { locationReload } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import salonSubscription from 'utils/salons/salonSubscription.util';

import CN from './TimeOffSplashStartFreeTrial.module.scss';

const CTAG = 'TimeOffSplashStartFreeTrial';
const L = logger(CTAG);

const SPINNER_COLOR = '#821bb8';
const SPINNER_BACKGROUND_COLOR = 'white';

const MODAL_TITLE_TRIAL = `Start your 30 day free trial ${S.party}`;
const MODAL_TEXT_TRIAL = [
  'You can choose from 1000s of emojis to customise your client tags. Apply tags to clients in their record card.',
  'This will allow you to quickly and easily see key information about clients on their bookings',
].join(' ');

const TimeOffSplashStartFreeTrial = ({
  salonId,
  errorMessage,
  beActivateTagsTrial,
  beGetTagsTrial,
  hasBilling,
  pricingPlan,
}) => {
  const { val: isTrialRendered, yay: showTrial, nay: hideTrial } = useTip(false);
  const { val: isLoading, yay: startLoading, nay: stopLoading } = useTip(false);
  const { val: isErrorRendered, tip: toggleError } = useTip(false);
  const { val: isSuccessRendered, yay: showSuccess, nay: hideSuccess } = useTip(false);

  const handleStartClick = useCallback(async () => {
    try {
      startLoading();
      const trialStateResult = await beGetTagsTrial({ salonId });
      const name = getAnyOrVoid('response.all_features.0', trialStateResult);
      const activateResult = await beActivateTagsTrial({ salonId, name });

      if (activateResult.error) {
        toggleError();
        hideTrial();
      } else {
        delayed(DELAY.flash, () => {
          showSuccess();
          locationReload(new FeStack({ tag: 'TimeOffSplashStartFreeTrial__start-click' }));
        });
      }
    } catch (e) {
      L.warn('beActivateTagsTrial()', e);
    } finally {
      stopLoading();
      hideSuccess();
    }
  }, [
    beGetTagsTrial,
    salonId,
    beActivateTagsTrial,
    startLoading,
    stopLoading,
    toggleError,
    hideTrial,
    hideSuccess,
    showSuccess,
  ]);

  const isComponentRendered = salonSubscription(hasBilling, pricingPlan);

  if (!isComponentRendered) {
    return null;
  }

  return (
    <div data-bem="TimeOffSplashStartFreeTrial">
      <div className={CN.component}>
        <Button variant="slim" onClick={showTrial}>
          <small className={CN.textSm}>START FREE TRIAL</small>
        </Button>
      </div>

      {isLoading && <Spinner color={SPINNER_COLOR} message="" background={SPINNER_BACKGROUND_COLOR} />}

      {fromAnyToBool(isErrorRendered) && (
        <SalonModal tag="TimeOffSplashStartFreeTrialError" isOpen={isErrorRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}> Promotion has already been activated</span>
            <Space>
              <span className={CN.modalText}>{errorMessage}</span>
            </Space>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={toggleError}>
              CLOSE
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {fromAnyToBool(isSuccessRendered) && (
        <SalonModal tag="TimeOffSplashStartFreeTrialSuccess" isOpen={isSuccessRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>Promotion has been activated</span>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={hideSuccess}>
              CLOSE
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {fromAnyToBool(isTrialRendered) && (
        <SalonModal tag="StartFreeTrial" isOpen={isTrialRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>{MODAL_TITLE_TRIAL}</span>
            <Space>
              <span className={CN.modalText}>{MODAL_TEXT_TRIAL}</span>
            </Space>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={hideTrial}>
              CANCEL
            </Button>
            <Button variant="primary" casing="dialog" onClick={handleStartClick}>
              Start free trial
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  errorMessage: selectBillingError(state),
  trialResponseText: selectBillingTrial(state),
  billingPlan: selectBillingPlan(state),
  hasBilling: selectCurrentSalonBilling(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beGetTagsTrial,
      beActivateTagsTrial,
    },
    dispatch,
  );

const TimeOffSplashStartFreeTrialHoc = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TimeOffSplashStartFreeTrial),
);

export default TimeOffSplashStartFreeTrialHoc;
